import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'

const API = 'https://zumo-api.megan.qzz.io'

export default function SignupPage() {
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [googleLoading, setGoogleLoading] = useState(false)
  const { setUser } = useAuthStore()
  const navigate = useNavigate()

  const handleSignup = async (e: FormEvent) => {
    e.preventDefault(); setError('')
    if (password !== confirm) { setError('Passwords do not match'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return }
    setLoading(true)
    try {
      const res = await fetch(`${API}/v1/auth/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, phone: phone || undefined }),
      })
      const data = await res.json()
      if (data.token) { localStorage.setItem('zumo_token', data.token); setUser(data.user); navigate('/chat') }
      else setError(data.error || 'Signup failed')
    } catch { setError('Network error') }
    setLoading(false)
  }

  const handleGoogle = () => {
    setGoogleLoading(true)
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.onload = () => {
      (window as any).google?.accounts.id.initialize({
        client_id: '599593422056-nt8u5hocpec5d5vh0hv2q3qfdvlqpj8e.apps.googleusercontent.com',
        callback: async (res: any) => {
          try {
            const r = await fetch(`${API}/v1/auth/oauth/google`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idToken: res.credential }) })
            const d = await r.json()
            if (d.token) { localStorage.setItem('zumo_token', d.token); setUser(d.user); navigate('/chat') }
            else setError(d.error || 'Google sign-in failed')
          } catch { setError('Network error') }
          setGoogleLoading(false)
        },
      })
      (window as any).google?.accounts.id.prompt()
    }
    script.onerror = () => { setGoogleLoading(false); setError('Failed to load Google Sign-In') }
    document.head.appendChild(script)
  }

  return (
    <div style={s.container}>
      <div style={s.card}>
        <h1 style={s.logo}>Zumo</h1>
        <p style={s.sub}>Create your free account</p>

        <button onClick={handleGoogle} disabled={loading || googleLoading} style={{ ...s.oauthBtn, opacity: loading || googleLoading ? 0.6 : 1 }}>
          <span style={{ ...s.oauthIcon, background: '#4285F4' }}>G</span>
          {googleLoading ? 'Loading...' : 'Continue with Google'}
        </button>

        <div style={s.divider}>
          <span style={s.line} /><span style={s.divText}>or sign up with email</span><span style={s.line} />
        </div>

        {error && <div style={s.error}>{error}<button onClick={() => setError('')} style={s.errClose}>×</button></div>}

        <form onSubmit={handleSignup} style={s.form}>
          <label style={s.label}>Full Name</label>
          <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} style={s.input} placeholder="John Doe" required />
          <label style={s.label}>Username</label>
          <input type="text" value={username} onChange={e => setUsername(e.target.value)} style={s.input} placeholder="johndoe" required minLength={3} />
          <label style={s.label}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={s.input} placeholder="john@email.com" required />
          <label style={s.label}>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={s.input} placeholder="Min 6 chars" required minLength={6} />
          <label style={s.label}>Confirm Password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={s.input} placeholder="Re-type" required minLength={6} />
          <label style={s.label}>Phone <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} style={s.input} placeholder="+254 712 345 678" />
          <button type="submit" disabled={loading} style={{ ...s.btn, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p style={s.footer}>Already have an account? <Link to="/login" style={s.link}>Log in</Link></p>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  container: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', width: '100%', background: 'var(--bg-primary)', padding: 20 },
  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: 'clamp(20px,4vw,32px)', maxWidth: 420, width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  logo: { fontSize: 28, fontWeight: 800, textAlign: 'center', marginBottom: 4, background: 'linear-gradient(135deg,#00d158,#00ff6e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  sub: { color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 18, fontSize: 14 },
  oauthBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 12, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 12, color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer', width: '100%', marginBottom: 14, transition: 'opacity 0.2s' },
  oauthIcon: { width: 24, height: 24, borderRadius: 12, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  divider: { display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 14px' },
  line: { flex: 1, height: 1, background: 'var(--border)' },
  divText: { color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  label: { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 },
  input: { padding: '10px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, outline: 'none', width: '100%' },
  btn: { padding: 14, background: 'var(--accent-glow)', border: '1px solid rgba(0,255,106,0.3)', borderRadius: 12, color: 'var(--accent)', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 4 },
  error: { background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  errClose: { background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18 },
  footer: { color: 'var(--text-muted)', textAlign: 'center', marginTop: 18, fontSize: 13 },
  link: { color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 },
}
